import React, { useEffect, useCallback } from 'react'
import { withRouter } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import {
  fetchOrganization,
  setTrialAsOver,
  getTrialState,
  getCurrentOrganization,
  getTrialRemainingTime,
  isFeatureEnabled,
  getActiveState,
} from 'ducks/organizations'
import { getApiKey } from 'ducks/apikeys'
import useHandleTrialOrUpgrade from 'hooks/useHandleTrialOrUpgrade'

import Loading from '../Loading'
import Button from '../Button'
import EmptyState from '../EmptyState'

import { generateText } from './utils'
import './FreeTrial.scss'

const useTrialIsOverChecks = () => {
  const dispatch = useDispatch()
  const timeToEnd = useSelector(getTrialRemainingTime)
  let timeoutId

  const addCheck = useCallback(() => {
    if (timeToEnd > 0) {
      timeoutId = setTimeout(() => {
        dispatch(setTrialAsOver())
      }, timeToEnd)
    }
  }, [dispatch, timeToEnd])

  const removeCheck = () => {
    clearTimeout(timeoutId)
  }

  return {
    addTrialIsOverCheck: addCheck,
    removeTrialIsOverCheck: removeCheck,
  }
}

const useFreeTrialData = appId => {
  const dispatch = useDispatch()

  const { addTrialIsOverCheck, removeTrialIsOverCheck } = useTrialIsOverChecks()

  const { isPaid, appApiKey } = useSelector(state => ({
    appApiKey: getApiKey(state, appId),
    isPaid: getActiveState(state, appId),
  }))

  const organization = useSelector(getCurrentOrganization)
  const { trialState, days, trialEndTimestamp } = useSelector(getTrialState)

  useEffect(() => {
    if (organization?.id) {
      dispatch(fetchOrganization(organization.id))
      addTrialIsOverCheck()
    }

    return removeTrialIsOverCheck
  }, [dispatch, organization?.id, trialEndTimestamp])

  return { isPaid, appApiKey, trialState, days }
}

const getColor = type => {
  return {
    orange: type === 'externalCollection',
    darkPink: type === 'customFonts',
    purple: type === 'api',
    teal: type === 'designVersions',
  }
}

const parseType = type => {
  const customIntegrations = ['customAction', 'externalCollection', 'api']

  return customIntegrations.includes(type) ? 'customIntegrations' : type
}

const FreeTrial = ({ appId, type }) => {
  const { isPaid, appApiKey, trialState, days } = useFreeTrialData(appId)

  const parsedType = parseType(type)

  const isEnabled = useSelector(state => isFeatureEnabled(state, parsedType))

  const handleTrial = useHandleTrialOrUpgrade(
    appId,
    trialState,
    type,
    appApiKey
  )

  if (
    (isEnabled && isPaid) ||
    (type === 'customAction' && trialState === 'before')
  ) {
    return null
  }

  const loading = !trialState

  if (loading) {
    return <Loading />
  }

  const parsedState = !isEnabled && isPaid ? 'upgrade' : trialState

  const { header, body, button, icon } = generateText(days, type, parsedState)
  const color = getColor(type)

  return (
    <EmptyState
      bordered
      className={`data-upgrade-prompt new-trial ${parsedState} ${type}-${parsedState}-free-trial-prompt ${
        appApiKey ? 'free-trial-with-api-key' : ''
      }`}
    >
      <div className="prompt-header">
        {icon && (
          <span role="img" aria-label="pen">
            {icon}
          </span>
        )}
        <h2>{header}</h2>
      </div>
      <p>{body}</p>
      <Button
        className={`prompt-button-${type}`}
        outlined={trialState === 'during'}
        small
        onClick={handleTrial}
        {...color}
      >
        {button}
      </Button>
    </EmptyState>
  )
}

export default withRouter(FreeTrial)
